import { Heading } from '@chakra-ui/react';
import { ActionButtons, Form } from '@repo/shared/shared';
import { useTranslation } from 'next-i18next';
import { useEffect, useId } from 'react';
import { useForm } from 'react-hook-form';
import { cartStoreSelectors, useSubscribeCartStoreWithShallow } from '@/entities/cart';
import { ProductType } from '@/entities/product';
import { DefaultContainer } from '@/shared/ui';
import useProductSubmit from '../../hooks/useProductSubmit';
import CustomBlocks from './AccordionBlocks/AccordionBlocks';
import BusinessSearchField from './BusinessSearchField';
import ColorSelector from './ColorSelector';
import ListItems from './ListItems';
import ProductImageGallery from './ProductImageGallery';
import ProductQuantity from './QuantitySelector';
import SelectedProductPrice from './SelectedProductPrice';

type FormValues = {
  googleBusinessProfile: { value: string; label: string } | null;
};

interface ProductBlockProps {
  product: ProductType;
}

const ProductBlock = ({ product }: ProductBlockProps) => {
  const { t } = useTranslation();
  const productName = product.name;
  const googleProfile = useSubscribeCartStoreWithShallow(cartStoreSelectors.googleProfile);
  const isHydrated = useSubscribeCartStoreWithShallow(cartStoreSelectors.hydrated);

  const onSubmit = useProductSubmit({
    name: productName,
    slug: product.slug,
    price: product.price,
    variants: product.variants,
    commonImages: product.commonImages,
    quantityDiscounts: product.quantityDiscounts,
  });

  const formId = useId();
  const methods = useForm<FormValues>({
    defaultValues: {
      googleBusinessProfile: null,
    },
  });

  useEffect(() => {
    if (isHydrated && googleProfile) {
      methods.reset({
        googleBusinessProfile: {
          value: googleProfile.placeId,
          label: googleProfile.name,
        },
      });
    }
  }, [isHydrated, googleProfile, methods]);

  const availableColors = product.variants.map((variant) => variant.color);
  const hasColorVariants = availableColors.length > 0;

  return (
    <DefaultContainer
      alignItems={{ base: 'center', md: 'flex-start' }}
      containerProps={{
        _odd: {
          bg: 'bg.light',
        },
      }}
      flexDirection={{ base: 'column', md: 'row' }}
      gap='3xl'
    >
      <ProductImageGallery
        commonImages={product.commonImages}
        flex={1}
        imgSizes={{
          base: 'calc(100vw - 1.5rem * 2)',
          sm: '30rem',
          md: 'calc(50vw - 3rem * 2 - 4rem)',
          lg: `calc(90rem - 3rem * 2 - 4rem)`,
        }}
        maxW={{ base: '30rem', md: 'unset' }}
        position='sticky'
        top={{ base: 'unset', md: '5rem' }}
        variants={product.variants}
        w='full'
      />
      <Form
        childrenContainerProps={{
          alignItems: 'start',
          flexDirection: 'column',
          gap: 'md',
          w: 'full',
          flex: 1,
        }}
        formId={formId}
        methods={methods}
        onSubmit={onSubmit}
      >
        <Heading as='h2'>{productName}</Heading>
        <SelectedProductPrice
          price={product.price}
          quantityDiscounts={product.quantityDiscounts}
          slug={product.slug}
        />
        <ListItems items={product.benefits} />
        <ProductQuantity
          price={product.price}
          quantityDiscounts={product.quantityDiscounts}
          slug={product.slug}
        />
        {hasColorVariants && <ColorSelector colors={availableColors} />}
        <BusinessSearchField />
        <ActionButtons
          mt='xl'
          positiveButtonProps={{
            size: 'lg',
            w: 'full',
            type: 'submit',
          }}
          positiveButtonText={t('store.product.addToCart')}
          w='full'
        />
        <CustomBlocks
          customBlocks={product.customBlocks}
          description={product.description}
          techSpecs={product.techSpecs}
        />
      </Form>
    </DefaultContainer>
  );
};

export default ProductBlock;
